import React, { useCallback } from 'react'
import { Currency, Trade, JSBI, TokenAmount, CurrencyAmount } from 'vvs-sdk'
import { useDispatch, useSelector } from 'react-redux'
import { getZapEstimatorContract } from 'utils/contractHelpers'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useCurrency } from 'hooks/Tokens'
import { useTradeExactIn } from 'hooks/Trades'
import { wrappedCurrency } from 'utils/wrappedCurrency'
import BigNumber from 'bignumber.js'
import { tryParseAmount } from 'state/swap/hooks'
import { computeTradePriceBreakdown, warningSeverity } from 'utils/prices'
import FormattedPriceImpact from 'views/Swap/components/FormattedPriceImpact'

import { useTranslation } from 'contexts/Localization'
import { AppDispatch, AppState } from '../index'
import { typeInput } from './actions'

const zapEstimatorContract = getZapEstimatorContract()

export function useZapState(): AppState['zap'] {
  return useSelector<AppState, AppState['zap']>((state) => state.zap)
}

export function useZapActionHandlers(): {
  onUserInput: (typedValue: string) => void
} {
  const dispatch = useDispatch<AppDispatch>()

  const onUserInput = useCallback(
    (typedValue: string) => {
      dispatch(typeInput({ typedValue }))
    },
    [dispatch],
  )

  return {
    onUserInput,
  }
}

// FIXME: Currently only factors in a max of 3 estimation routes returned from smart contract. To use loops (cant use hooks) if possible
export const useTrades = (props) => {
  const { t } = useTranslation()
  const { amount, currCurrency, swapPathBeforeLP, swapAmountsBeforeLP, pair } = props

  const trades: Trade[] = []
  const { chainId } = useActiveWeb3React()

  const intermediateCurrency1 = useCurrency(swapPathBeforeLP?.[1] ?? undefined)
  const intermediateCurrency2 = useCurrency(swapPathBeforeLP?.[2] ?? undefined)
  let intermediateAmount1 = swapAmountsBeforeLP?.length >= 2 ? swapAmountsBeforeLP?.[1] : undefined
  let intermediateAmount2 = swapAmountsBeforeLP?.length >= 3 ? swapAmountsBeforeLP?.[2] : undefined
  intermediateAmount1 =
    intermediateAmount1 &&
    intermediateCurrency1 &&
    !new BigNumber(intermediateAmount1._hex).isZero() &&
    new TokenAmount(
      wrappedCurrency(intermediateCurrency1, chainId),
      tryParseAmount(
        new BigNumber(intermediateAmount1._hex)
          .multipliedBy(new BigNumber(10).exponentiatedBy(-intermediateCurrency1.decimals))
          .toString(),
        intermediateCurrency1,
      ).raw,
    )
  intermediateAmount2 =
    intermediateAmount2 &&
    intermediateCurrency2 &&
    !new BigNumber(intermediateAmount2._hex).isZero() &&
    new TokenAmount(
      wrappedCurrency(intermediateCurrency2, chainId),
      tryParseAmount(
        new BigNumber(intermediateAmount2._hex)
          .multipliedBy(new BigNumber(10).exponentiatedBy(-intermediateCurrency2.decimals))
          .toString(),
        intermediateCurrency2,
      ).raw,
    )

  let currAmount = swapAmountsBeforeLP?.length > 0 ? swapAmountsBeforeLP?.[0] : amount
  const token0Currency = useCurrency(pair?.token0.address ?? undefined)
  const token1Currency = useCurrency(pair?.token1.address ?? undefined)

  const halfIntermediateAmount = intermediateAmount2
    ? new TokenAmount(
        wrappedCurrency(intermediateCurrency2, chainId),
        JSBI.divide(intermediateAmount2?.raw, JSBI.BigInt(2)),
      )
    : intermediateAmount1
    ? new TokenAmount(
        wrappedCurrency(intermediateCurrency1, chainId),
        JSBI.divide(intermediateAmount1?.raw, JSBI.BigInt(2)),
      )
    : null

  const halfIntermediateToken0Trade = useTradeExactIn(halfIntermediateAmount ?? undefined, token0Currency, true)
  const halfIntermediateToken1Trade = useTradeExactIn(halfIntermediateAmount ?? undefined, token1Currency, true)

  currAmount =
    currAmount instanceof CurrencyAmount && currCurrency
      ? currAmount
      : currAmount &&
        currCurrency &&
        !new BigNumber(currAmount._hex).isZero() &&
        new TokenAmount(
          wrappedCurrency(currCurrency, chainId),
          tryParseAmount(
            new BigNumber(currAmount._hex)
              .multipliedBy(new BigNumber(10).exponentiatedBy(-currCurrency.decimals))
              .toString(),
            currCurrency,
          ).raw,
        )
  const currToken0Trade = useTradeExactIn(currAmount, token0Currency, true)
  const currToken1Trade = useTradeExactIn(currAmount, token1Currency, true)

  const halfCurrAmount =
    currAmount && new TokenAmount(wrappedCurrency(currCurrency, chainId), JSBI.divide(currAmount.raw, JSBI.BigInt(2)))
  const halfCurrToken0Trade = useTradeExactIn(halfCurrAmount ?? undefined, token0Currency, true)
  const halfCurrToken1Trade = useTradeExactIn(halfCurrAmount ?? undefined, token1Currency, true)

  const latestIntermediateCurrency = intermediateCurrency2 ?? intermediateCurrency1

  const currIntermediate1Trade = useTradeExactIn(amount, intermediateCurrency1, true)
  const intermediate1Intermediate2Trade = useTradeExactIn(intermediateAmount1, intermediateCurrency2, true)

  const steps = [{ action: 'deposit', percentage: null, symbol: currCurrency?.symbol }]
  const stepsAmounts = [amount?.toSignificant(6)]
  let nextSwapCurrency: Currency = wrappedCurrency(currCurrency, chainId)

  const swapPercentages = []
  // Handle case for zap A -> LP B-C
  if (nextSwapCurrency?.symbol !== pair?.token0.symbol && nextSwapCurrency?.symbol !== pair?.token1.symbol) {
    if (intermediateCurrency1 && currIntermediate1Trade) {
      trades.push(currIntermediate1Trade)
      steps.push({
        action: 'swap',
        percentage: '100%',
        symbol: `${currCurrency.symbol === 'CRO' ? 'WCRO' : currCurrency.symbol} > ${
          intermediateCurrency1.symbol === 'CRO' ? 'WCRO' : intermediateCurrency1.symbol
        }`,
      })
      stepsAmounts.push(currAmount?.toSignificant(6))
      swapPercentages.push('100%')
      nextSwapCurrency = intermediateCurrency1
    }
    if (intermediateCurrency2 && intermediate1Intermediate2Trade) {
      trades.push(intermediate1Intermediate2Trade)
      steps.push({
        action: 'swap',
        percentage: '100%',
        symbol: `${intermediateCurrency1.symbol === 'CRO' ? 'WCRO' : intermediateCurrency1.symbol} > ${
          intermediateCurrency2.symbol === 'CRO' ? 'WCRO' : intermediateCurrency2.symbol
        }`,
      })
      stepsAmounts.push(intermediateAmount1?.toSignificant(6))
      swapPercentages.push('100%')
      nextSwapCurrency = intermediateCurrency2
    }
    // Get trade object for 50% amount swaps
    if (nextSwapCurrency?.symbol !== pair?.token0.symbol) {
      if (latestIntermediateCurrency && halfIntermediateToken0Trade) {
        trades.push(halfIntermediateToken0Trade)
        steps.push({
          action: 'swap',
          percentage: '50%',
          symbol: `${latestIntermediateCurrency.symbol === 'CRO' ? 'WCRO' : latestIntermediateCurrency.symbol} > ${
            token0Currency.symbol === 'CRO' ? 'WCRO' : token0Currency.symbol
          }`,
        })
        stepsAmounts.push(halfIntermediateAmount?.toSignificant(6))
        swapPercentages.push('50%')
      } else if (currCurrency && halfCurrToken0Trade) {
        trades.push(halfCurrToken0Trade)
        steps.push({
          action: 'swap',
          percentage: '50%',
          symbol: `${currCurrency.symbol === 'CRO' ? 'WCRO' : currCurrency.symbol} > ${
            token0Currency.symbol === 'CRO' ? 'WCRO' : token0Currency.symbol
          }`,
        })
        stepsAmounts.push(halfCurrAmount?.toSignificant(6))
        swapPercentages.push('50%')
      }
    }
    if (nextSwapCurrency?.symbol !== pair?.token1.symbol) {
      if (latestIntermediateCurrency && halfIntermediateToken1Trade) {
        trades.push(halfIntermediateToken1Trade)
        steps.push({
          action: 'swap',
          percentage: '50%',
          symbol: `${latestIntermediateCurrency.symbol === 'CRO' ? 'WCRO' : latestIntermediateCurrency.symbol} > ${
            token1Currency.symbol === 'CRO' ? 'WCRO' : token1Currency.symbol
          }`,
        })
        stepsAmounts.push(halfIntermediateAmount?.toSignificant(6))
        swapPercentages.push('50%')
      } else if (currCurrency && halfCurrToken1Trade) {
        trades.push(halfCurrToken1Trade)
        steps.push({
          action: 'swap',
          percentage: '50%',
          symbol: `${nextSwapCurrency.symbol === 'CRO' ? 'WCRO' : nextSwapCurrency.symbol} > ${
            token1Currency.symbol === 'CRO' ? 'WCRO' : token1Currency.symbol
          }`,
        })
        stepsAmounts.push(halfCurrAmount?.toSignificant(6))
        swapPercentages.push('50%')
      }
    }
  } else {
    // Handle case for zap A -> LP A-B or LP B-A
    if (nextSwapCurrency?.symbol !== pair?.token0.symbol) {
      if (currToken0Trade) {
        trades.push(currToken0Trade)
        steps.push({
          action: 'swap',
          percentage: '50%',
          symbol: `${currCurrency.symbol === 'CRO' ? 'WCRO' : currCurrency.symbol} > ${
            token0Currency.symbol === 'CRO' ? 'WCRO' : token0Currency.symbol
          }`,
        })
        stepsAmounts.push(currAmount?.toSignificant(6))
        swapPercentages.push('50%')
      }
    }
    if (nextSwapCurrency?.symbol !== pair?.token1.symbol) {
      if (currToken1Trade) {
        trades.push(currToken1Trade)
        steps.push({
          action: 'swap',
          percentage: '50%',
          symbol: `${currCurrency.symbol === 'CRO' ? 'WCRO' : currCurrency.symbol} > ${
            token1Currency.symbol === 'CRO' ? 'WCRO' : token1Currency.symbol
          }`,
        })
        stepsAmounts.push(currAmount?.toSignificant(6))
        swapPercentages.push('50%')
      }
    }
  }

  let priceImpactSeverity = 0
  let currPriceImpactWithoutFee
  const swapTrades = trades.map((trade, index) => {
    const { priceImpactWithoutFee, realizedLPFee } = computeTradePriceBreakdown(trade)
    const currentPriceImpactSeverity = warningSeverity(priceImpactWithoutFee)
    if (currentPriceImpactSeverity > priceImpactSeverity) {
      priceImpactSeverity = currentPriceImpactSeverity
      currPriceImpactWithoutFee = priceImpactWithoutFee
    }
    const priceImpact = <FormattedPriceImpact priceImpact={priceImpactWithoutFee} />
    const liquidityProviderFee = realizedLPFee
      ? `${realizedLPFee.toSignificant(4)} ${trade?.inputAmount.currency.symbol}`
      : '-'
    return {
      token0: trade?.route?.path[0],
      token1: trade?.route?.path[trade?.route?.path.length - 1],
      route: t('Swap %parsedPercentage% %tokenRoute%', {
        parsedPercentage: swapPercentages[index],
        tokenRoute: `${trade?.route?.path[0].symbol} > ${trade?.route?.path[trade?.route?.path.length - 1].symbol}`,
      }),
      price: t('%amount% %token0Symbol% per %token1Symbol%', {
        amount: trade?.executionPrice?.invert().toSignificant(6),
        token0Symbol: wrappedCurrency(trade?.executionPrice.baseCurrency, chainId)?.symbol,
        token1Symbol: wrappedCurrency(trade?.executionPrice.quoteCurrency, chainId)?.symbol,
      }),
      priceImpact,
      liquidityProviderFee,
    }
  })
  return { steps, stepsAmounts, swapTrades, priceImpactSeverity, currPriceImpactWithoutFee }
}

export const fetchEstimateAddLiquidityOutputAmount = async (
  amountForToken0: string,
  amountForToken1: string,
  lpAddress: string,
) => {
  try {
    const res = await zapEstimatorContract.estimateAddLiquidityOutputAmount(amountForToken0, amountForToken1, lpAddress)
    return res
  } catch (e) {
    console.error(e)
    return null
  }
}

export const fetchEstimateZapInToLpSwapPaths = async (fromToken: string, inputAmount: string, lpAddress: string) => {
  try {
    const res = await zapEstimatorContract.estimateZapInToLpSwapPaths(fromToken, inputAmount, lpAddress)
    return {
      swapPathBeforeLP: res[0],
      swapAmountsBeforeLP: res[1],
      amountForToken0: res[2],
      amountForToken1: res[3],
    }
  } catch (e) {
    console.error(e)
    return null
  }
}
